import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { t } from '../i18n';
import api from '../utils/api';
import { format, parseISO } from 'date-fns';
import ChatPanel from '../components/ChatPanel';
import LunchCalendar from '../components/LunchCalendar';
import { downloadMatchICS } from '../utils/ics';

export default function DashboardPage() {
  const { user, refreshUser } = useAuth();
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [availability, setAvailability] = useState([]);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get('/matches')
      .then(r => setMatches(r.data))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (user) {
      setAvailability(user.availability?.map(d => typeof d === 'string' ? d.split('T')[0] : d) || []);
    }
  }, [user]);

  const saveAvailability = async () => {
    await api.put('/profile/availability', { dates: availability });
    await refreshUser();
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const openChat = useCallback((match) => {
    setActiveChat(match);
    // The chat panel's initial message fetch marks the match read server-side;
    // zero the badge locally right away instead of waiting on a refetch.
    setMatches(prev => prev.map(m => m.id === match.id ? { ...m, unread_count: 0 } : m));
  }, []);

  const hasUnread = (match) => match.unread_count > 0;

  const upcoming = matches
    .filter(m => new Date(m.matched_date) >= new Date().setHours(0,0,0,0))
    .sort((a, b) => new Date(a.matched_date) - new Date(b.matched_date));
  const past = matches.filter(m => new Date(m.matched_date) < new Date().setHours(0,0,0,0));

  return (
    <div style={{ padding: '32px 0', minHeight: '80vh' }}>
      <div className="container">
        {/* Welcome */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, marginBottom: 6 }}>{t('welcomeBack')}, {user?.name?.split(' ')[0]}! 👋</h1>
          {!loading && (
            <p style={{ color: 'var(--text-muted)' }}>
              {matches.length === 0 ? t('noMatchesYet') : t('matchesIntro')}
            </p>
          )}
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 60, color: 'var(--text-muted)' }}>
            {t('loadingMatches')}
          </div>
        ) : matches.length === 0 ? (
          <div>
            <div style={{ marginBottom: 28 }}>
              <p style={{ color: 'var(--text-muted)' }}>{t('noMatchesInfo')}</p>
            </div>
            <div style={{ marginBottom: 28 }}>
              <h2 style={{ fontSize: 20, marginBottom: 6 }}>{t('profileAvailability')}</h2>
              <p style={{ color: 'var(--text-muted)' }}>{t('profileAvailabilityInfo')}</p>
            </div>
            <div className="card" style={{ padding: 32, maxWidth: 560 }}>
              <LunchCalendar selected={availability} onChange={setAvailability} bookedMatches={[]} />
              <div style={{ marginTop: 24 }}>
                <button className="btn btn-primary" onClick={saveAvailability} style={{ minWidth: 140 }}>
                  {saved ? t('profileSaved') : t('profileSaveChanges')}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            {upcoming.length > 0 && (
              <section>
                <h2 style={{ fontSize: 20, marginBottom: 16 }}>{t('upcomingLunches')}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {upcoming.map(m => (
                    <MatchCard key={m.id} match={m} unread={hasUnread(m)} onChat={() => openChat(m)} />
                  ))}
                </div>
              </section>
            )}
            {past.length > 0 && (
              <section>
                <h2 style={{ fontSize: 20, marginBottom: 16, color: 'var(--text-muted)' }}>{t('pastLunches')}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {past.map(m => (
                    <MatchCard key={m.id} match={m} unread={hasUnread(m)} onChat={() => openChat(m)} past />
                  ))}
                </div>
              </section>
            )}
          </div>
        )}
      </div>

      {activeChat && (
        <ChatPanel match={activeChat} onClose={() => setActiveChat(null)} />
      )}
    </div>
  );
}

function MatchCard({ match, onChat, past, unread }) {
  const dateStr = format(parseISO(match.matched_date), 'EEEE, MMMM d, yyyy');
  const participants = match.participants || [];

  return (
    <div className="card dashboard-card" style={{
      padding: 24, display: 'flex', gap: 20, alignItems: 'flex-start',
      opacity: past ? 0.75 : 1
    }}>
      {/* Stacked avatars */}
      <div style={{ display: 'flex', flexShrink: 0 }}>
        {participants.map((p, i) => (
          <div key={p.id} className="avatar" style={{
            background: p.color, width: 52, height: 52, fontSize: 20,
            marginLeft: i > 0 ? -14 : 0,
            border: '2px solid white',
            zIndex: participants.length - i
          }}>
            {p.name[0]}
          </div>
        ))}
      </div>

      <div style={{ flex: 1 }}>
        <div style={{ marginBottom: 6 }}>
          {participants.map(p => (
            <div key={p.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600, fontSize: 17 }}>{p.name}</span>
              <a href={`mailto:${p.email}`} style={{ fontSize: 13, color: 'var(--text-muted)', textDecoration: 'none' }}
                onMouseOver={e => e.currentTarget.style.textDecoration = 'underline'}
                onMouseOut={e => e.currentTarget.style.textDecoration = 'none'}>
                {p.email}
              </a>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
          {match.group_size > 2 && (
            <span className="badge" style={{ fontSize: 12, background: 'var(--green-100)', color: 'var(--match-a)' }}>
              {match.group_size}-person lunch
            </span>
          )}
          {!past && <span className="badge" style={{ fontSize: 12 }}>🗓 {dateStr}</span>}
          {past && <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{dateStr}</span>}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
          {match.shared_languages?.map(l => (
            <span key={l.name} className="badge" style={{ background: '#eff6ff', color: '#1d4ed8', fontSize: 12 }}>
              🗣 {l.name}
            </span>
          ))}
          {match.bu_info?.my_bu_name && (
            <span className="badge" style={{ fontSize: 12 }}>
              {match.bu_info.my_bu_name}
            </span>
          )}
          {match.bu_info?.participant_bu_names?.filter(Boolean).map((name, i) =>
            name !== match.bu_info.my_bu_name ? (
              <span key={i} className="badge" style={{ fontSize: 12 }}>{name}</span>
            ) : null
          )}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flexShrink: 0 }}>
        <button
          className={`btn btn-outline ${unread ? 'btn-chat-unread' : ''}`}
          style={{ position: 'relative' }}
          onClick={onChat}
        >
          💬 {t('chat')}
          {unread && (
            <span style={{
              position: 'absolute', top: -5, right: -5,
              width: 12, height: 12, borderRadius: '50%',
              background: '#ef4444', border: '2px solid white'
            }} />
          )}
        </button>
        {!past && (
          <button className="btn btn-outline" onClick={() => downloadMatchICS(match)}>
            📅 {t('addToCalendar')}
          </button>
        )}
      </div>
    </div>
  );
}
